import { api } from '@/shared/axios-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

export type DeletionMode = 'soft' | 'hard';

export interface DeleteComponentInput {
  branchId: string;
  deletionMode: DeletionMode;
}

interface DeleteResponse {
  success?: boolean;
  message: string;
  deletionMode?: DeletionMode;
  status?: 'deleted' | 'pending_deinstrument';
  deinstrument_jobs?: string[];
  pendingDeletion?: {
    branchId?: string;
    status?: string;
    deletionMode?: DeletionMode;
    targets?: string[];
  };
}

function deleteErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail ?? err.response?.data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      const errObj = detail as { Error?: string };
      if (typeof errObj.Error === 'string' && errObj.Error.trim()) {
        return errObj.Error;
      }
      try {
        return JSON.stringify(detail);
      } catch {
        /* fall through */
      }
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Delete failed';
}

/** Delete an onboarded branch card (awaits deinstrument PR merge when we raised it). */
const deleteComponent = async ({
  branchId,
  deletionMode,
}: DeleteComponentInput): Promise<DeleteResponse> => {
  if (!branchId?.trim()) {
    throw new Error('Missing branchId — cannot delete this component card.');
  }
  try {
    const { data } = await api.delete<DeleteResponse>(
      `/api/repositories/${branchId}`,
      { params: { deletionMode } },
    );
    return data;
  } catch (err) {
    throw new Error(deleteErrorMessage(err));
  }
};

export const useDeleteComponent = () => {
  const queryClient = useQueryClient();
  return useMutation<DeleteResponse, Error, DeleteComponentInput>({
    mutationFn: deleteComponent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['component-list'] });
    },
  });
};
